import { Button, Form, Modal, Row } from "antd";
import { routes } from "config/routes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { ADDRESS_TYPES, toBackendAddressType } from "utils/addressType";
import { getFormError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useClientsApi from "views/Clients/hooks/useClientsApi";
import useCoreStore from "views/hooks/useCoreStore";
import useAccountingApi from "views/Settings/Accounting/hooks/useAccountingApi";
import CreateClientForm from "./Forms/CreateClientForm";

interface CreateClientPropsTypes {
  id?: ID;
  refetch: (orderBy?: string[] | undefined) => void;
}

export default function CreateClient(props: CreateClientPropsTypes): React.ReactElement {
  const { id, refetch } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { notificationError, notificationSuccessfullySaved } = useNotification();
  const companySelected = useCoreStore(state => state.companySelected);

  const {
    createUpdateClient,
    createEntityAddress,
    createUpdateClientLoading,
    createEntityAddressLoading,
  } = useClientsApi();
  const { getCompanyPlatform } = useAccountingApi();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const clientType = Form.useWatch("clientType", form);
  const isParticularClientType = clientType === "pa";

  useEffect(() => {
    if (isModalVisible) {
      getCompanyPlatform({
        variables: {
          id: companySelected?.id,
        },
      });
    }
  }, [isModalVisible]);

  const onValuesChange = (changedValues: Record<string, any>, allValues: Record<string, any>) => {
    if (changedValues?.clientType === "pa") {
      form.setFields([
        {
          name: "vatNumber",
          value: {
            label: "0000000000 00000",
            value: "0000000000 00000",
          },
        },
        {
          name: "euVatNumber",
          value: "",
        },
      ]);
    }

    if (isParticularClientType && changedValues?.clientType) {
      form.setFields([
        {
          name: "vatNumber",
          value: {
            label: "",
            value: "",
          },
        },
        {
          name: "euVatNumber",
          value: "",
        },
      ]);
    }

    if (changedValues?.country && changedValues?.country?.value !== "FR") {
      form.resetFields(["vatNumber", "euVatNumber"]);
    }
    if (changedValues?.postalCode) {
      form.setFieldsValue({
        ...allValues,
        city: {
          label: changedValues?.postalCode.data.city,
          value: changedValues?.postalCode.data.city,
          data: null,
        },
      });
    }

    if (changedValues?.city?.value && !allValues.postalCode) {
      form.setFieldsValue({
        ...allValues,
        postalCode: {
          label: `${changedValues?.city.data.postalCode} - ${changedValues?.city.data.city}`,
          value: `${changedValues?.city.data.postalCode} - ${changedValues?.city.data.city}`,
          data: null,
        },
      });
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const onFinish = async (values: Record<string, any>) => {
    let errors: any = [];

    const rawVat = (values?.vatNumber?.value ?? values?.vatNumber ?? "").replace(/\s/g, "");

    const newVatNumber = rawVat ? rawVat.slice(0, 9) : undefined;
    const newNicNumber = rawVat.length > 9 ? rawVat.slice(9, 14) : undefined;

    const variables = {
      input: {
        clientType: values.clientType,
        currencySettingCode: values.currency,
        code: values.code,
        name: values.name,
        vatNumber: newVatNumber,
        euVatNumber: values.euVatNumber,
        nic: values?.headquartersNic || newNicNumber,
        chorus: values.chorus,
        paymentMethodId: values.paymentMethodId,
        legalStructureId: values?.legalStructure?.value,
        paymentDeadline: values.paymentDeadline?.value,
        clientVatTypeId: values.clientVatTypeId?.value,
        counterpartCode: values.counterpartCode,
        company: companySelected?.id,
      },
    };

    const response = await createUpdateClient({
      variables: variables as unknown as NonNullable<
        Parameters<typeof createUpdateClient>[0]
      >["variables"],
    });
    const responseData = response?.data as
      { createClientInfo?: { clientInfo?: { id?: ID }; errors?: any[] } } | undefined;
    const clientId = responseData?.createClientInfo?.clientInfo?.id;

    const hasGraphQLErrors = response?.data === undefined;

    errors = responseData?.createClientInfo?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else if (hasGraphQLErrors) {
      notificationError(response?.errors as unknown as Parameters<typeof notificationError>[0]);
    } else {
      const variables = {
        input: {
          entity: clientId,
          streetName: values.streetName,
          addressBtq: values.addressBtq,
          addressNr: values.addressNr,
          postalCode: values.postalCode?.value || "",
          city: values.city?.value,
          streetNameComplement: values.streetNameComplement,
          addressType: toBackendAddressType(ADDRESS_TYPES.MAIN, "entities"),
          name: values.name,
          countryId: values?.country?.value || "FR",
        },
      };

      await createEntityAddress({ variables });

      notificationSuccessfullySaved(t("Client"));
      setIsModalVisible(false);
      refetch!();
      form.resetFields();
      routes.navigate(`/company/${companySelected?.id}/clients/${clientId}/client`);
    }
  };

  return (
    <>
      {!id && (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}
      <Modal
        title={t("Add client")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Save")}
        okButtonProps={{
          loading: createUpdateClientLoading || createEntityAddressLoading,
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        width={768}
        className="antd-modal"
      >
        <CreateClientForm form={form} onFinish={onFinish} onValuesChange={onValuesChange} />
      </Modal>
    </>
  );
}
